import { useEffect, useId, useRef, useState } from 'react'
import { useIsCompact } from '../../app/media'
import { useFocusTrap } from '../../app/useFocusTrap'
import { useStrings } from '../../i18n'
import { Legend } from '../../legend/Legend'
import { selectFilters, useAppStore } from '../../state/store'
import { FilterPanelBody } from './FilterPanelBody'
import { countActiveFilters } from './facets'
import './filters.css'

/**
 * MOUNT SLOT - the filter panel.
 *
 * Desktop gets a persistent side panel. Below the shell's one breakpoint the
 * same panel becomes a bottom sheet behind a toggle, so the map keeps the screen
 * and the controls stay in thumb reach. The sheet is hidden rather than
 * unmounted: dismissing it leaves the filters, the query and the half-typed
 * search term exactly as they were.
 *
 * The legend rides along on narrow screens. On a wide screen it lives in the
 * right-hand rail beside the detail panel; on a phone that rail is a sheet that
 * only appears once a site is picked, and a key you cannot reach until after
 * you have used the thing it explains is not a key. It is the same component in
 * both places, mounted once.
 */

export const FilterPanel = () => {
  const strings = useStrings()
  const compact = useIsCompact()
  const [open, setOpen] = useState(false)
  const sheetId = useId()
  const toggleRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLElement>(null)

  const activeCount = countActiveFilters(useAppStore(selectFilters))

  // The open sheet covers the map, so Tab has to stay inside it: everything
  // behind is visually hidden, and focus that lands there is focus the visitor
  // cannot see.
  useFocusTrap(sheetRef, compact && open)

  // Opening the sheet moves focus into it, and closing it puts focus back on
  // the button that opened it - otherwise a keyboard user lands at the top of
  // the document every time they dismiss it.
  useEffect(() => {
    if (!compact) return
    if (open) sheetRef.current?.focus()
  }, [open, compact])

  const close = (): void => {
    setOpen(false)
    toggleRef.current?.focus()
  }

  if (!compact)
    return (
      <section className="filters__rail" aria-label={strings.filters.title}>
        <h2 className="slot__title">{strings.filters.title}</h2>
        <FilterPanelBody />
      </section>
    )

  return (
    <div className="filters__compact">
      <button
        ref={toggleRef}
        className="filters__toggle"
        type="button"
        aria-expanded={open}
        aria-controls={sheetId}
        onClick={() => setOpen(!open)}
      >
        <span>{strings.filters.showFilters}</span>
        {activeCount === 0 ? null : <span className="filters__badge">{activeCount}</span>}
      </button>

      {/* A modal dialog while it is open: it covers the map, so it takes both
          the screen reader's cursor and the Tab key with it. */}
      <section
        ref={sheetRef}
        className="filters__sheet"
        id={sheetId}
        role="dialog"
        aria-modal={open}
        aria-label={strings.filters.title}
        tabIndex={-1}
        hidden={!open}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          // A dialog opened from inside this panel - the charge-card editor -
          // is portaled to `document.body` so the sheet's `hidden` attribute
          // and its scroll container cannot reach it, but a portal still
          // bubbles through the React tree it was declared in. Without this,
          // one Escape would close the dialog *and* the sheet behind it.
          if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return
          close()
        }}
      >
        <div className="filters__sheet-header">
          <span className="filters__grabber" aria-hidden="true" />
          <h2 className="slot__title">{strings.filters.title}</h2>
          <button className="ev-button ev-button--quiet" type="button" onClick={close}>
            {strings.common.close}
          </button>
        </div>

        <FilterPanelBody />
        <Legend />
      </section>
    </div>
  )
}
